/**
 * SmartOmnibar — the persistent floating "one-click generation" launcher.
 *
 * Bottom-right on every authed page. One click opens a menu of the
 * most common one-shot generation shortcuts. Every item deep-links
 * to the existing endpoint / page — the omnibar is a discovery
 * surface, not a new engine.
 *
 * Hidden on:
 *   • Non-authed routes (Landing, Blog, /vs, /integrate, /best, /pricing…)
 *   • The Copilot Hub page itself (would be redundant)
 *   • Admin routes (already dense enough)
 *
 * Respects `prefers-reduced-motion` (skips the pulse animation).
 *
 * "Smart" / "Copilot" / "Auto" wording only — no "AI" text per the
 * app's PRD rule.
 */
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, X, ClipboardList, FileText, Send, MessageSquare,
  Wrench, Home, DollarSign, Search,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import "./SmartAccent.css";

const HIDE_ROUTES = [
  /^\/$/, /^\/pricing/, /^\/blog/, /^\/vs/, /^\/integrate/, /^\/best/,
  /^\/features/, /^\/answers/, /^\/press/, /^\/landlord-software/,
  /^\/property-management/, /^\/admin/, /^\/login/, /^\/signup/,
  /^\/get-started/, /^\/copilot/, /^\/about/, /^\/careers/, /^\/legal/,
  /^\/privacy/, /^\/terms/, /^\/lp\b/, /^\/ads\b/,
  /^\/tools\b/, /^\/university/, /^\/laws/,
];

const ITEMS = [
  { icon: Home,          label: "Draft a new listing",           to: "/properties/new",                  role: "landlord" },
  { icon: FileText,      label: "Generate a lease",              to: "/lease/new",                       role: "landlord" },
  { icon: ClipboardList, label: "Screen a tenant applicant",     to: "/applications",                    role: "landlord" },
  { icon: Send,          label: "Draft a rent reminder",         to: "/copilot?intent=rent-reminder",    role: "landlord" },
  { icon: FileText,      label: "Generate provincial notice",    to: "/copilot?intent=notice",           role: "landlord" },
  { icon: DollarSign,    label: "Scan portfolio for rent gains", to: "/revenue-optimizer",               role: "landlord" },
  { icon: Wrench,        label: "Open a maintenance ticket",     to: "/maintenance/new",                 role: "tenant" },
  { icon: MessageSquare, label: "Message my landlord",           to: "/messages",                        role: "tenant" },
  { icon: Search,        label: "Ask Copilot anything",          to: "/copilot",                         role: "any" },
];

export default function SmartOmnibar() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pathname, setPathname] = useState(typeof window !== "undefined" ? window.location.pathname : "/");
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem("crc_omnibar_hidden") === "1"; } catch { return false; }
  });
  const dismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    try { localStorage.setItem("crc_omnibar_hidden", "1"); } catch {}
    setHidden(true);
  };
  const menuRef = useRef(null);

  useEffect(() => {
    const onNav = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onNav);
    // React Router uses pushState — we poll every 1.2s for path changes.
    const interval = window.setInterval(onNav, 1200);
    return () => {
      window.removeEventListener("popstate", onNav);
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Route filter — hide entirely on public / admin / login routes.
  if (HIDE_ROUTES.some((rx) => rx.test(pathname))) return null;

  // User dismissed via the X — respect until they explicitly re-enable.
  if (hidden) return null;

  // Non-authed users — hide entirely (no shortcuts make sense).
  if (!user) return null;

  const role = user.role || "any";
  const items = ITEMS.filter((i) => i.role === "any" || i.role === role);
  if (items.length === 0) return null;

  return (
    <div
      ref={menuRef}
      data-testid="smart-omnibar"
      className="fixed bottom-24 right-4 sm:right-6 z-40"
    >
      {open && (
        <div
          data-testid="smart-omnibar-menu"
          className="mb-3 w-72 rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden smart-accent-ring"
        >
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-violet-700 inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Copilot shortcuts
            </div>
            <p className="text-xs text-slate-600 mt-1">One click and Copilot drafts it for you.</p>
          </div>
          <div className="max-h-96 overflow-y-auto py-1">
            {items.map((it, i) => {
              const Icon = it.icon;
              return (
                <button
                  key={i}
                  onClick={() => { setOpen(false); navigate(it.to); }}
                  data-testid={`smart-omnibar-item-${i}`}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-emerald-100 via-sky-100 to-violet-100 flex-none">
                    <Icon className="w-4 h-4 text-emerald-700" />
                  </div>
                  <span className="text-sm text-slate-800 font-semibold">{it.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Open Copilot shortcuts"
        aria-expanded={open}
        data-testid="smart-omnibar-fab"
        className={`inline-flex items-center gap-2 px-4 py-3 rounded-full smart-accent-btn text-sm font-bold ${open ? "" : "smart-accent-glow"}`}
      >
        {open ? <X className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
        <span className="hidden sm:inline">{open ? "Close" : "Copilot"}</span>
      </button>
      <button
        onClick={dismiss}
        data-testid="smart-omnibar-dismiss"
        aria-label="Hide Copilot"
        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white text-slate-700 border border-slate-300 shadow-md flex items-center justify-center hover:bg-slate-100 z-10"
      >
        <X className="w-3.5 h-3.5"/>
      </button>
    </div>
  );
}
